import { BookOpen, CalendarDays, Settings, Sun } from "lucide-react";
import { useMemo } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { localePath, useLocale, useMessages } from "../i18n";
import { buildPrivateHead } from "../seo";
import { ARTICLES_PATH } from "../shared";
import { useAppStore } from "../store/appStore";
import { Logo } from "./Logo";
import { Seo } from "./Seo";

export function AppShell() {
  const persistenceWarning = useAppStore((state) => state.persistenceWarning);
  const locale = useLocale();
  const t = useMessages();

  /**
   * The app's own pages carry a learner's settings and progress, so none of
   * them is public. One head covers all of them: it says noindex, and which
   * language the app is speaking, and nothing else.
   */
  const head = useMemo(() => buildPrivateHead("Your sheets", locale), [locale]);

  const navItems = [
    { to: "/today", label: t.app.today, icon: Sun },
    { to: "/week", label: t.app.thisWeek, icon: CalendarDays },
    {
      to: localePath(locale, ARTICLES_PATH),
      label: t.nav.guides,
      icon: BookOpen,
    },
    { to: "/settings", label: t.app.settings, icon: Settings },
  ];

  return (
    <div className="shell">
      <Seo head={head} />
      <header className="shell__header screen-only">
        {/* The wordmark goes home, the way it does on every other shell. */}
        <Link to={localePath(locale, "/")} aria-label="SprinkleDays home">
          <Logo />
        </Link>

        <nav className="shell__nav" aria-label={t.app.mainNav}>
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `shell__link${isActive ? " shell__link--active" : ""}`
              }
            >
              <Icon size={16} aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </header>

      {persistenceWarning ? (
        <p className="notice screen-only" role="status">
          {persistenceWarning}
        </p>
      ) : null}

      <Outlet />
    </div>
  );
}
